import React, { useEffect, useMemo, useState } from 'react';
import api from '../api/api';

/** SAP-style golden arrow (opens list chooser). */
export function SapYellowArrow({ onClick, title, disabled }) {
  return (
    <button
      type="button"
      onClick={disabled ? undefined : onClick}
      title={title}
      disabled={disabled}
      className={`flex-shrink-0 flex items-center justify-center w-5 h-5 transition-opacity ${
        disabled ? 'opacity-40 cursor-not-allowed' : 'hover:opacity-80'
      }`}
    >
      <svg width="12" height="12" viewBox="0 0 14 14" className="drop-shadow-sm">
        <polygon points="1,1 13,7 1,13" fill="#E8A000" stroke="#C47800" strokeWidth="0.8" strokeLinejoin="round" />
      </svg>
    </button>
  );
}

function isActiveEmployeeRow(e) {
  if (!e) return false;
  if (e.active === undefined || e.active === null) return true;
  return Number(e.active) === 1 || String(e.active).toLowerCase() === 'tyes';
}

function SalesEmployeePickerDialog({ current, onSelect, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/sales-employees');
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const list = rows.filter(isActiveEmployeeRow).filter((e) => String(e.employee_name || '').trim());
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => {
      const name = String(e.employee_name || '').toLowerCase();
      const rem = String(e.remarks || '').toLowerCase();
      return name.includes(q) || rem.includes(q);
    });
  }, [rows, search]);

  const pick = (name) => {
    onSelect(String(name || '').trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl border border-sap-border w-[640px] max-h-[520px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Sales employees</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center text-xs text-sap-textLight">Loading…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Name</th>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Remarks</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const name = String(e.employee_name || '').trim();
                  const isCur = name === String(current || '').trim();
                  const isSel = selected && String(selected.employee_name || '').trim() === name;
                  return (
                    <tr
                      key={`${e.id ?? i}-${name}`}
                      onClick={() => setSelected(e)}
                      onDoubleClick={() => pick(name)}
                      className={`border-t border-sap-border/20 cursor-pointer transition-colors ${
                        isSel ? 'bg-sap-blue/15 ring-1 ring-sap-blue/30' : isCur ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-sap-text">{name}</td>
                      <td className="px-3 py-2 text-sap-textLight">{e.remarks || '—'}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={2} className="px-3 py-6 text-center text-sap-textLight">No employees found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between">
          <span className="text-2xs text-sap-textLight">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => selected && pick(selected.employee_name)}
              disabled={!selected}
              className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark disabled:opacity-40 transition-all"
            >
              Choose
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

function ActiveUserPickerDialog({ current, usersUrl, onSelect, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(usersUrl);
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, [usersUrl]);

  const filtered = useMemo(() => {
    const q = search.trim().toLowerCase();
    const names = [...new Set(rows.map((u) => String(u.name || '').trim()).filter(Boolean))].sort((a, b) =>
      a.localeCompare(b)
    );
    if (!q) return names;
    return names.filter((n) => n.toLowerCase().includes(q));
  }, [rows, search]);

  const pick = (name) => {
    onSelect(String(name || '').trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl border border-sap-border w-[520px] max-h-[480px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Users (active)</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center text-xs text-sap-textLight">Loading…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Name</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((name) => {
                  const isCur = name === String(current || '').trim();
                  const isSel = selected?.name === name;
                  return (
                    <tr
                      key={name}
                      onClick={() => setSelected({ name })}
                      onDoubleClick={() => pick(name)}
                      className={`border-t border-sap-border/20 cursor-pointer transition-colors ${
                        isSel ? 'bg-sap-blue/15 ring-1 ring-sap-blue/30' : isCur ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-sap-text">{name}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td className="px-3 py-6 text-center text-sap-textLight">No users found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between">
          <span className="text-2xs text-sap-textLight">{filtered.length} user{filtered.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => selected && pick(selected.name)}
              disabled={!selected}
              className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark disabled:opacity-40 transition-all"
            >
              Choose
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

/**
 * Dropdown of active sales employees + golden arrow → chooser modal.
 */
export function SalesEmployeeSelectWithArrow({ value, onChange, selectClassName, disabled }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/sales-employees');
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const names = useMemo(() => {
    const ns = rows
      .filter(isActiveEmployeeRow)
      .map((e) => String(e.employee_name || '').trim())
      .filter(Boolean);
    return [...new Set(ns)].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const cur = String(value ?? '').trim();
  const orphan = cur && !names.includes(cur);

  return (
    <>
      <div className="flex items-center gap-1 w-full min-w-0">
        <select
          value={cur}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={selectClassName}
        >
          <option value="">— Select —</option>
          {orphan ? <option value={cur}>{cur}</option> : null}
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <SapYellowArrow title="Choose sales employee" disabled={disabled} onClick={() => !disabled && setOpen(true)} />
      </div>
      {open && (
        <SalesEmployeePickerDialog
          current={cur}
          onSelect={(v) => onChange(v)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Dropdown of active app users + golden arrow → chooser (usersUrl e.g. "/purchase-orders/users").
 */
export function ActiveUserSelectWithArrow({ value, onChange, usersUrl, selectClassName, disabled }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get(usersUrl);
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, [usersUrl]);

  const names = useMemo(
    () => [...new Set(rows.map((u) => String(u.name || '').trim()).filter(Boolean))].sort((a, b) => a.localeCompare(b)),
    [rows]
  );

  const cur = String(value ?? '').trim();
  const orphan = cur && !names.includes(cur);

  return (
    <>
      <div className="flex items-center gap-1 w-full min-w-0">
        <select
          value={cur}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={selectClassName}
        >
          <option value="">— Select —</option>
          {orphan ? <option value={cur}>{cur}</option> : null}
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <SapYellowArrow title="Choose user" disabled={disabled} onClick={() => !disabled && setOpen(true)} />
      </div>
      {open && (
        <ActiveUserPickerDialog
          current={cur}
          usersUrl={usersUrl}
          onSelect={(v) => onChange(v)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

/**
 * Dropdown of active employees (OHEM/sap_employees) + golden arrow → chooser modal.
 */
export function SapEmployeeSelectWithArrow({ value, onChange, selectClassName, disabled }) {
  const [rows, setRows] = useState([]);
  const [open, setOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/sap-employees');
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRows([]);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const names = useMemo(() => {
    const ns = rows
      .filter(isActiveEmployeeRow)
      .map((e) => `${e.first_name} ${e.last_name}`.trim())
      .filter(Boolean);
    return [...new Set(ns)].sort((a, b) => a.localeCompare(b));
  }, [rows]);

  const cur = String(value ?? '').trim();
  const orphan = cur && !names.includes(cur);

  return (
    <>
      <div className="flex items-center gap-1 w-full min-w-0">
        <select
          value={cur}
          disabled={disabled}
          onChange={(e) => onChange(e.target.value)}
          className={selectClassName}
        >
          <option value="">— Select —</option>
          {orphan ? <option value={cur}>{cur}</option> : null}
          {names.map((n) => (
            <option key={n} value={n}>
              {n}
            </option>
          ))}
        </select>
        <SapYellowArrow title="Choose employee" disabled={disabled} onClick={() => !disabled && setOpen(true)} />
      </div>
      {open && (
        <SapEmployeePickerDialog
          current={cur}
          onSelect={(v) => onChange(v)}
          onClose={() => setOpen(false)}
        />
      )}
    </>
  );
}

function SapEmployeePickerDialog({ current, onSelect, onClose }) {
  const [rows, setRows] = useState([]);
  const [loading, setLoading] = useState(true);
  const [search, setSearch] = useState('');
  const [selected, setSelected] = useState(null);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const r = await api.get('/sap-employees');
        if (!cancelled) setRows(Array.isArray(r) ? r : []);
      } catch {
        if (!cancelled) setRows([]);
      } finally {
        if (!cancelled) setLoading(false);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  const filtered = useMemo(() => {
    const list = rows.filter(isActiveEmployeeRow);
    const q = search.trim().toLowerCase();
    if (!q) return list;
    return list.filter((e) => {
      const first = String(e.first_name || '').toLowerCase();
      const last = String(e.last_name || '').toLowerCase();
      return first.includes(q) || last.includes(q);
    });
  }, [rows, search]);

  const pick = (emp) => {
    onSelect(`${emp.first_name} ${emp.last_name}`.trim());
    onClose();
  };

  return (
    <div className="fixed inset-0 z-[80] flex items-center justify-center bg-black/30" onClick={onClose}>
      <div
        className="bg-white rounded-xl shadow-2xl border border-sap-border w-[640px] max-h-[520px] flex flex-col"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between px-4 py-2.5 bg-sap-blue rounded-t-xl">
          <span className="text-sap-text text-xs font-semibold">Employees</span>
          <button type="button" onClick={onClose} className="text-sap-textLight hover:text-sap-text text-base leading-none">✕</button>
        </div>
        <div className="px-4 py-2 border-b border-sap-border">
          <input
            autoFocus
            value={search}
            onChange={(e) => setSearch(e.target.value)}
            placeholder="Search by name…"
            className="w-full h-7 px-2 text-xs border border-sap-border rounded focus:outline-none focus:border-sap-blue"
          />
        </div>
        <div className="overflow-y-auto flex-1">
          {loading ? (
            <div className="py-10 text-center text-xs text-sap-textLight">Loading…</div>
          ) : (
            <table className="w-full text-xs">
              <thead className="sticky top-0 bg-sap-blue/5 border-b border-sap-border/40">
                <tr>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">Last Name</th>
                  <th className="text-left px-3 py-2 text-2xs font-semibold text-sap-textLight uppercase tracking-wider">First Name</th>
                </tr>
              </thead>
              <tbody>
                {filtered.map((e, i) => {
                  const fullName = `${e.first_name} ${e.last_name}`.trim();
                  const isCur = fullName === String(current || '').trim();
                  const isSel = selected && selected.employee_id === e.employee_id;
                  return (
                    <tr
                      key={`${e.employee_id ?? i}`}
                      onClick={() => setSelected(e)}
                      onDoubleClick={() => pick(e)}
                      className={`border-t border-sap-border/20 cursor-pointer transition-colors ${
                        isSel ? 'bg-sap-blue/15 ring-1 ring-sap-blue/30' : isCur ? 'bg-sap-blue/10 font-semibold' : 'hover:bg-sap-blue/5'
                      }`}
                    >
                      <td className="px-3 py-2 font-medium text-sap-text">{e.last_name || '—'}</td>
                      <td className="px-3 py-2 text-sap-text">{e.first_name || '—'}</td>
                    </tr>
                  );
                })}
                {filtered.length === 0 && (
                  <tr><td colSpan={2} className="px-3 py-6 text-center text-sap-textLight">No employees found</td></tr>
                )}
              </tbody>
            </table>
          )}
        </div>
        <div className="px-4 py-2 border-t border-sap-border bg-gray-50 rounded-b-xl flex items-center justify-between">
          <span className="text-2xs text-sap-textLight">{filtered.length} row{filtered.length !== 1 ? 's' : ''}</span>
          <div className="flex gap-2">
            <button
              type="button"
              onClick={() => selected && pick(selected)}
              disabled={!selected}
              className="px-4 py-1.5 text-xs font-semibold bg-sap-blue text-white rounded-lg hover:bg-sap-blueDark disabled:opacity-40 transition-all"
            >
              Choose
            </button>
            <button
              type="button"
              onClick={onClose}
              className="px-4 py-1.5 text-xs font-semibold border border-sap-border rounded-lg hover:bg-gray-100 transition-all"
            >
              Cancel
            </button>
          </div>
        </div>
      </div>
    </div>
  );
}

